import React, { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Button, Card, Chip, Grid, Stack, TableContainer, Typography } from '@mui/material'

import { useGetUserProfileQuery } from '../../../../api/combined'
import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { LockedFeature } from '../../../../components/LockedFeature/LockedFeature'
import NotAvailableIcon from '../../../../components/NotAvailableIcon/NotAvailableIcon'
import lockedFeatureService from '../../../../services/LockedFeatureService'
import { LockedFeatureId } from '../../../../types/LockedFeature'
import { Setting } from '../../../../types/Setting'
import { TableSortValues } from '../../../../types/TableSortValues'
import { useAllGamesUnlockedCheck } from '../../../account/hooks/roleHooks'
import { GameSearchDialogConfig, GameSearchDialogType } from '../../../game-search/types/GameSearchDialogConfig'
import { GameAnalysisOutdatedIndicator } from '../../../game/components/GameAnalysisOutdatedIndicator/GameAnalysisOutdatedIndicator'
import GameIcon from '../../../game/components/GameIcon/GameIcon'
import { GameLink } from '../../../game/components/GameLink/GameLink'
import { GamePowerscore } from '../../../game/components/GamePowerscore/GamePowerscore'
import { Game } from '../../../game/types/Game'
import type { GameWithMarketIso } from '../GameSearchDialog/GameSearchDialog'
import GameSearchResultFollowGameButton from '../GameSearchFollowGameButton/GameSearchFollowGameButton'
import styles from './GameSearchResultsTable.module.scss'

export interface OpenGamesForEveryone {
  openGamesSetting?: Setting
  featuredGamesSetting?: Setting
}

// the discontinued section is collapsed by default so it doesn't compete for attention with active games
const discontinuedSectionCollapsedByDefault = ['discontinued']

type GameSearchResultsTableProps = {
  games: Game[]
  marketIso: string
  gameSearchDialogConfig: Pick<GameSearchDialogConfig, 'type' | 'gameSelectorCounts' | 'trackedGamesPermissionMap' | 'trackedGamesDiscontinuedMap'>
  selectedGames: { [gameId: string]: Game }
  isLoading?: boolean
  hideRankData?: boolean
  onGameSelect: (game: Game) => void
  onSelectAllGames?: () => void
  onGameDeselect?: (game: Game) => void
  gamesWithMarketIso?: GameWithMarketIso
  enableMarketSelector?: boolean
  selectedGamesCount?: number
  gamesOpenForEveryone?: OpenGamesForEveryone
  hidePowerscore?: boolean
  displayAppType?: boolean
  displayNotAvailableIcon?: boolean
  freeLiveEventsGames?: Game[]
}

/**
 * Component for displaying Game Search Results Table
 */
export const GameSearchResultsTable: React.FC<GameSearchResultsTableProps> = memo(
  ({
    games,
    marketIso,
    isLoading,
    hideRankData,
    gameSearchDialogConfig,
    selectedGames,
    onGameSelect,
    onGameDeselect,
    onSelectAllGames,
    gamesWithMarketIso,
    enableMarketSelector,
    selectedGamesCount = 0,
    gamesOpenForEveryone,
    hidePowerscore,
    displayAppType,
    displayNotAvailableIcon = false,
    freeLiveEventsGames,
  }) => {
    const MAX_RANK = 1000
    const { t } = useTranslation()
    const [lockedFeatureDialogOpen, setLockedFeatureDialogOpen] = useState<boolean>(false)
    const allGamesUnlocked = useAllGamesUnlockedCheck()
    const { data: currentUser } = useGetUserProfileQuery()

    const isGameSelected = useCallback(
      (game: Game, props?: typeof customProps) => {
        if (enableMarketSelector && props && props?.gamesWithMarketIso && props?.gamesWithMarketIso[game.id] && props?.gamesWithMarketIso[game.id].length) {
          return props.gamesWithMarketIso[game.id].includes(props.marketIso)
        }
        return props?.selectedGames[game.id]
      },
      [enableMarketSelector]
    )

    const isGameUnlocked = useCallback(
      (game: Game) => {
        if (allGamesUnlocked) return true
        if (game && game.owner === currentUser?.organization.id) return true

        const isInFreeList = freeLiveEventsGames && freeLiveEventsGames.some((freeGame) => freeGame.id === game.id)
        if (isInFreeList) return true

        return game.isUnlocked() || game.isOpenForEveryone(gamesOpenForEveryone?.openGamesSetting, gamesOpenForEveryone?.featuredGamesSetting)
      },
      [allGamesUnlocked, currentUser?.organization.id, freeLiveEventsGames, gamesOpenForEveryone?.openGamesSetting, gamesOpenForEveryone?.featuredGamesSetting]
    )

    const hasGamePermission = useCallback(
      (game: Game) => {
        // Check if game is in free live events games list
        const isInFreeList = freeLiveEventsGames && freeLiveEventsGames.some((freeGame) => freeGame.id === game.id)
        if (isInFreeList) return true

        // Check tracked games permission map
        if (gameSearchDialogConfig.trackedGamesPermissionMap) return gameSearchDialogConfig.trackedGamesPermissionMap[game.id]
        return false
      },
      [gameSearchDialogConfig.trackedGamesPermissionMap, freeLiveEventsGames]
    )

    const isGameDiscontinued = useCallback(
      (game: Game) => !!gameSearchDialogConfig.trackedGamesDiscontinuedMap?.[game.id],
      [gameSearchDialogConfig.trackedGamesDiscontinuedMap]
    )

    const cellClass = useCallback(
      (game: Game) => {
        // Check if game is in free live events games list
        const isInFreeList = freeLiveEventsGames && freeLiveEventsGames.some((freeGame) => freeGame.id === game.id)
        if (isInFreeList) return ''

        // Check if game has permission in tracked games permission map
        return gameSearchDialogConfig.trackedGamesPermissionMap && !gameSearchDialogConfig.trackedGamesPermissionMap[game.id]
          ? styles['no-permission-content-style']
          : ''
      },
      [gameSearchDialogConfig.trackedGamesPermissionMap, freeLiveEventsGames]
    )

    const shouldShowRowAlwaysLast = useCallback(
      (game: Game) => {
        const isInFreeList = freeLiveEventsGames && freeLiveEventsGames.some((freeGame) => freeGame.id === game.id)
        if (isInFreeList) return false
        return gameSearchDialogConfig.trackedGamesPermissionMap && !gameSearchDialogConfig.trackedGamesPermissionMap[game.id] ? true : false
      },
      [gameSearchDialogConfig.trackedGamesPermissionMap, freeLiveEventsGames]
    )

    const userHasLimitedPermission = useMemo(
      () => gameSearchDialogConfig.trackedGamesPermissionMap && Object.values(gameSearchDialogConfig.trackedGamesPermissionMap).some(Boolean),
      [gameSearchDialogConfig.trackedGamesPermissionMap]
    )

    const shouldShowRowAlwaysFirst = useCallback(
      (game: Game) => {
        // Only pin free games to top when user has limited permissions
        if (userHasLimitedPermission) return false
        const isInFreeList = freeLiveEventsGames && freeLiveEventsGames.some((freeGame) => freeGame.id === game.id)
        return !!isInFreeList
      },
      [freeLiveEventsGames, userHasLimitedPermission]
    )

    // force discontinued games to the bottom of the table regardless of the selected sort
    // so they can be displayed as a separate section
    const discontinuedLastSortAccessor = useCallback(
      ({ row }: { row: Game }) => (isGameDiscontinued(row) ? TableSortValues.AlwaysLast : 0),
      [isGameDiscontinued]
    )

    const getRowSectionId = useCallback((game: Game) => (isGameDiscontinued(game) ? 'discontinued' : 'active'), [isGameDiscontinued])

    const renderSectionHeader = useCallback(
      ({ sectionId, count }: { sectionId: string; count: number }) => {
        const isDiscontinuedSection = sectionId === 'discontinued'
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: isDiscontinuedSection ? 'warning.main' : 'success.main' }} />
            <Typography variant="overline" sx={{ fontWeight: 'bold', lineHeight: 1 }} color="text.secondary">
              {isDiscontinuedSection ? t('live-events:discontinued_badge') : t('common:active')}
            </Typography>
            <Typography variant="overline" sx={{ lineHeight: 1 }} color="text.disabled">
              ({count})
            </Typography>
          </Stack>
        )
      },
      [t]
    )

    const customProps = useMemo(() => {
      return {
        selectedGames,
        gameSearchDialogConfig,
        handleOnGameSelect: onGameSelect,
        handleSelectAllGames: onSelectAllGames,
        handleOnGameDeselect: onGameDeselect,
        gamesWithMarketIso,
        marketIso,
        selectedGamesCount,
        isGameUnlocked,
        hasGamePermission,
        isGameDiscontinued,
        cellClass,
      }
    }, [
      isGameUnlocked,
      hasGamePermission,
      isGameDiscontinued,
      selectedGames,
      gameSearchDialogConfig,
      onGameSelect,
      onSelectAllGames,
      onGameDeselect,
      gamesWithMarketIso,
      marketIso,
      selectedGamesCount,
      cellClass,
    ])

    const tableColumns: GRTableColumn<Game, typeof customProps, string>[] = useMemo(
      () => [
        {
          id: 'game',
          labelAccessor: t('common:game'),
          accessor: ({ row: game, customTableProps }) => {
            const selectedMarketIso = customTableProps?.marketIso || marketIso

            return (
              <Grid container spacing={1.2} className={customTableProps?.cellClass(game)}>
                <Grid item>
                  <GameIcon
                    src={game.getIcon(false)}
                    gameName={game.resolvedName}
                    size="small"
                    appType={displayAppType ? game.appType : undefined}
                    gamePlatforms={game.platforms}
                    discontinued={customTableProps?.isGameDiscontinued(game)}
                  />
                </Grid>
                <Grid item xs>
                  <Stack>
                    {currentUser && game.isOutdatedForMarket(selectedMarketIso, currentUser) ? (
                      <div className={styles.gameContainer}>
                        <div>
                          <GRTooltip content={game.resolvedName}>
                            <Box className={styles.gameName}>{game.resolvedName}</Box>
                          </GRTooltip>
                          <GRTooltip content={game.artist}>
                            <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '150px' }}>{game.artist}</Box>
                          </GRTooltip>
                        </div>
                        <GameAnalysisOutdatedIndicator game={game} marketIso={selectedMarketIso} />
                      </div>
                    ) : (
                      <>
                        <GRTooltip content={game.resolvedName}>
                          <Box className={styles.gameName}>{game.resolvedName}</Box>
                        </GRTooltip>
                        <GRTooltip content={game.artist}>
                          <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '150px' }}>{game.artist}</Box>
                        </GRTooltip>
                      </>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            )
          },
          sortable: true,
          sortAccessor: [
            ({ row }) => row.name || '',
            ({ row }) => {
              if (shouldShowRowAlwaysFirst(row)) return TableSortValues.AlwaysFirst
              if (shouldShowRowAlwaysLast(row)) return TableSortValues.AlwaysLast

              return 0
            },
            discontinuedLastSortAccessor,
          ],
          cellProps: { align: 'left' },
          headerCellProps: { align: 'left', sx: { minWidth: 200 } },
          defaultSortOrder: SortOrder.DESC,
          sortOrder: hideRankData ? SortOrder.DESC : undefined,
        },
        {
          id: 'subgenre',
          labelAccessor: t('common:conventional_subgenre'),
          accessor: ({ row: game, customTableProps }) => {
            return (
              <div className={customTableProps?.cellClass(game)}>
                {game.conventionalSubgenre ? <Chip className={styles.subgenreChip} size="small" label={game.conventionalSubgenre}></Chip> : ''}
              </div>
            )
          },
          sortable: true,
          sortAccessor: [
            ({ row }) => row.conventionalSubgenre,
            ({ row }) => {
              if (shouldShowRowAlwaysFirst(row)) return TableSortValues.AlwaysFirst
              if (shouldShowRowAlwaysLast(row)) return TableSortValues.AlwaysLast

              return 0
            },
            discontinuedLastSortAccessor,
          ],
          cellProps: { align: 'center' },
          defaultSortOrder: SortOrder.DESC,
        },
        {
          id: 'gps',
          labelAccessor: t('common:game_power_score'),
          accessor: ({ row: game, customTableProps }) => (
            <div className={customTableProps?.cellClass(game)}>
              <GamePowerscore value={game.gpsPerMarket[customTableProps?.marketIso || marketIso]} />
            </div>
          ),
          sortable: true,
          sortAccessor: [
            ({ row, customTableProps }) => {
              const selectedMarketIso = customTableProps?.marketIso || marketIso
              return row.gpsPerMarket[selectedMarketIso] || 0
            },
            ({ row }) => {
              if (shouldShowRowAlwaysLast(row)) return TableSortValues.AlwaysLast
              return 0
            },
            discontinuedLastSortAccessor,
          ],
          cellProps: { align: 'center' },
          hidden: hidePowerscore ? true : false,
        },
        {
          id: 'sgr',
          labelAccessor: t('common:sustained_grossing_rank_shorthand'),
          accessor: ({ row: game, customTableProps }) => {
            return (
              <div className={customTableProps?.cellClass(game)}>
                {game.sranks[marketIso] || (!displayNotAvailableIcon && game.getSustainedGrossingRankForMarket(marketIso)) ? (
                  <strong>{game.sranks[marketIso] < MAX_RANK && game.sranks[marketIso] > 0 ? game.sranks[marketIso] : `${MAX_RANK}+`}</strong>
                ) : (
                  <NotAvailableIcon fontSize="18px" />
                )}
              </div>
            )
          },
          sortable: true,
          sortAccessor: [
            ({ row, customTableProps }) => {
              const selectedMarketIso = customTableProps?.marketIso || marketIso
              // Negate so SGR ASC uses higher PowerScore first as the tie-breaker.
              return -(row.gpsPerMarket[selectedMarketIso] || 0)
            },
            ({ row }) => {
              const srank = row.sranks[marketIso] || 0
              return srank > 0 && srank < MAX_RANK ? srank : MAX_RANK
            },
            ({ row }) => {
              if (shouldShowRowAlwaysFirst(row)) return TableSortValues.AlwaysFirst
              if (shouldShowRowAlwaysLast(row)) return TableSortValues.AlwaysLast

              return 0
            },
            discontinuedLastSortAccessor,
          ],
          cellProps: { align: 'center' },
          sortOrder: !hideRankData ? SortOrder.ASC : undefined,
          defaultSortOrder: SortOrder.ASC,
          hidden: hideRankData,
        },
        {
          id: 'sdr',
          labelAccessor: t('common:sustained_download_rank_shorthand'),
          accessor: ({ row: game, customTableProps }) => {
            return (
              <div className={customTableProps?.cellClass(game)}>
                {game.sdranks[marketIso] || (!displayNotAvailableIcon && game.getSustainedGrossingRankForMarket(marketIso)) ? (
                  <strong>{game.sdranks[marketIso] < MAX_RANK && game.sdranks[marketIso] > 0 ? game.sdranks[marketIso] : `${MAX_RANK}+`}</strong>
                ) : (
                  <NotAvailableIcon fontSize="18px" />
                )}
              </div>
            )
          },
          sortable: true,
          sortAccessor: [
            ({ row }) => {
              const sdrank = row.sdranks[marketIso] || 0
              return sdrank > 0 && sdrank < MAX_RANK ? sdrank : MAX_RANK
            },
            ({ row }) => {
              if (shouldShowRowAlwaysFirst(row)) return TableSortValues.AlwaysFirst
              if (shouldShowRowAlwaysLast(row)) return TableSortValues.AlwaysLast

              return 0
            },
            discontinuedLastSortAccessor,
          ],
          cellProps: { align: 'center' },
          defaultSortOrder: SortOrder.ASC,
          hidden: hideRankData,
        },
        {
          id: 'actions',
          labelAccessor: ({ customTableProps }) => {
            return (
              <>
                {customTableProps?.handleSelectAllGames && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => customTableProps?.handleSelectAllGames && customTableProps?.handleSelectAllGames()}
                  >
                    {t('common:select_all')}
                  </Button>
                )}
              </>
            )
          },
          accessor: ({ row: game, customTableProps }) => {
            return (
              <>
                {gameSearchDialogConfig.type === GameSearchDialogType.GameSearch && (
                  <GameLink game={game}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation()
                        customTableProps?.handleOnGameSelect(game)
                      }}
                    >
                      {t('common:open')}
                    </Button>
                  </GameLink>
                )}

                {gameSearchDialogConfig.type === GameSearchDialogType.GameBenchmark && (
                  <>
                    {isGameUnlocked(game) && !isGameSelected(game, customTableProps) && (
                      <div onClick={() => customTableProps?.handleOnGameSelect(game)}>
                        <Button size="small" variant="contained" color="success">
                          {t('common:select')}
                        </Button>
                      </div>
                    )}
                    {isGameUnlocked(game) && isGameSelected(game, customTableProps) && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        onClick={() => {
                          customTableProps?.handleOnGameDeselect && customTableProps?.handleOnGameDeselect(game)
                        }}
                      >
                        {t('common:deselect')}
                      </Button>
                    )}
                    {!isGameUnlocked(game) && (
                      <Button size="small" variant="contained" onClick={() => setLockedFeatureDialogOpen(true)}>
                        {t('common:unlock')}
                      </Button>
                    )}
                  </>
                )}

                {gameSearchDialogConfig.type === GameSearchDialogType.GameSelector && (
                  <>
                    {isGameUnlocked(game) && !isGameSelected(game, customTableProps) && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={
                          !customTableProps || customTableProps?.selectedGamesCount >= (customTableProps?.gameSearchDialogConfig.gameSelectorCounts?.max || 1)
                        }
                        onClick={() => customTableProps?.handleOnGameSelect(game)}
                      >
                        {t('common:select')}
                      </Button>
                    )}

                    {isGameUnlocked(game) && isGameSelected(game, customTableProps) && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        onClick={() => {
                          customTableProps?.handleOnGameSelect(game)
                        }}
                      >
                        {t('common:deselect')}
                      </Button>
                    )}

                    {!isGameUnlocked(game) && (
                      <Button size="small" variant="contained" onClick={() => setLockedFeatureDialogOpen(true)}>
                        {t('common:unlock')}
                      </Button>
                    )}
                  </>
                )}

                {gameSearchDialogConfig.type === GameSearchDialogType.GameFollow && (
                  <>
                    {!customTableProps?.selectedGames[game.id] && (
                      <GameSearchResultFollowGameButton
                        game={game}
                        onClick={() => {
                          customTableProps?.handleOnGameSelect(game)
                        }}
                      />
                    )}
                    {customTableProps?.selectedGames[game.id] && (
                      <Button size="small" variant="contained" color="warning" disabled>
                        {t('common:followed')}
                      </Button>
                    )}
                  </>
                )}

                {gameSearchDialogConfig.type === GameSearchDialogType.TrackedGames && (
                  <>
                    {customTableProps?.hasGamePermission(game) && !isGameSelected(game, customTableProps) && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={
                          !customTableProps || customTableProps?.selectedGamesCount >= (customTableProps?.gameSearchDialogConfig.gameSelectorCounts?.max || 1)
                        }
                        onClick={() => customTableProps?.handleOnGameSelect(game)}
                      >
                        {t('common:select')}
                      </Button>
                    )}

                    {customTableProps?.hasGamePermission(game) && isGameSelected(game, customTableProps) && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        onClick={() => {
                          customTableProps?.handleOnGameSelect(game)
                        }}
                      >
                        {t('common:deselect')}
                      </Button>
                    )}

                    {!customTableProps?.hasGamePermission(game) && (
                      <Button size="small" variant="contained" href={lockedFeatureService.contactUrl} target="_blank">
                        {t('common:unlock')}
                      </Button>
                    )}
                  </>
                )}
              </>
            )
          },
          sortable: false,
          cellProps: { align: 'center' },
          headerCellProps: { sx: { minWidth: '100px' } },
        },
      ],
      [
        t,
        hideRankData,
        hidePowerscore,
        marketIso,
        displayAppType,
        currentUser,
        shouldShowRowAlwaysLast,
        shouldShowRowAlwaysFirst,
        displayNotAvailableIcon,
        gameSearchDialogConfig.type,
        isGameUnlocked,
        isGameSelected,
        discontinuedLastSortAccessor,
      ]
    )

    const [columns, setColumns] = useState<GRTableColumn<Game, typeof customProps, string>[]>(tableColumns)
    const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<Game, typeof customProps, string>[]) => {
      setColumns(updatedColumns)
    }, [])

    const sortedGames = useMemo(() => {
      const activeSortId = columns.find((col) => col.sortOrder !== undefined)?.id
      if (activeSortId === 'subgenre' || activeSortId === 'sdr') {
        return [...games].sort((a, b) => {
          const aRank = a.sranks[marketIso] || 0
          const bRank = b.sranks[marketIso] || 0
          const aSgr = aRank > 0 && aRank < MAX_RANK ? aRank : MAX_RANK
          const bSgr = bRank > 0 && bRank < MAX_RANK ? bRank : MAX_RANK
          return aSgr - bSgr
        })
      }
      return games
    }, [games, marketIso, columns])

    return (
      <>
        <TableContainer component={Card} className={styles.container}>
          <GRTable
            rows={sortedGames}
            columns={columns}
            rowIdKey="id"
            striped
            hoverable
            onColumnsUpdated={handleColumnsUpdate}
            noRowsLabel={t('market-explorer:no_games_available')}
            isLoading={isLoading}
            customProps={customProps}
            getRowSectionId={gameSearchDialogConfig.trackedGamesDiscontinuedMap ? getRowSectionId : undefined}
            renderSectionHeader={renderSectionHeader}
            collapsibleSections
            defaultCollapsedSectionIds={discontinuedSectionCollapsedByDefault}
          />
        </TableContainer>
        <LockedFeature.Dialog
          lockedFeatureId={LockedFeatureId.GameUnlock}
          open={lockedFeatureDialogOpen}
          hideImage={true}
          onClose={() => setLockedFeatureDialogOpen(false)}
        />
      </>
    )
  }
)
